import { createTheme, type Theme } from '@mui/material/styles';

import type { ThemeMode } from './ThemeMode';

/**
 * Палитра shell для MUI. Все ключевые цвета берутся из набора shell-токенов,
 * которые также продублированы в CSS-переменных в tokens.css.
 */
const palettes = {
  light: {
    accent: '#10B981',
    accentHover: '#059669',
    accentPressed: '#047857',
    accentSubtle: '#ECFDF5',
    accentOn: '#FFFFFF',
    bgCanvas: '#FAFAF9',
    bgSurface: '#FFFFFF',
    bgSubtle: '#F0F0EE',
    bgHover: '#EBEBE9',
    border: '#D4D4D2',
    borderSubtle: '#EAEAE8',
    borderStrong: '#B8B8B5',
    textPrimary: '#1F2024',
    textSecondary: '#5A5C63',
    textTertiary: '#8A8C92',
    error: '#B91C1C',
    errorLight: '#FEF2F2',
    errorBorder: '#FECACA',
    successLight: '#F0FDF4',
    successBorder: '#BBF7D0',
    successText: '#166534',
    warningLight: '#FFFBEB',
    warningBorder: '#FDE68A',
    warningText: '#92400E',
    infoLight: '#EFF6FF',
    infoBorder: '#BFDBFE',
    infoText: '#1E40AF',
    actionHover: 'rgba(15, 16, 20, 0.04)',
    overlayShadow: '0 12px 32px rgba(15,16,20,0.12)',
  },
  dark: {
    accent: '#34D399',
    accentHover: '#6EE7B7',
    accentPressed: '#A7F3D0',
    accentSubtle: '#0F2920',
    accentOn: '#052E1B',
    bgCanvas: '#0F1014',
    bgSurface: '#17181D',
    bgSubtle: '#1E2026',
    bgHover: '#23252C',
    border: '#32343C',
    borderSubtle: '#26282F',
    borderStrong: '#454751',
    textPrimary: '#E8E8EA',
    textSecondary: '#A0A2A8',
    textTertiary: '#6D6F76',
    error: '#EF4444',
    errorLight: 'rgba(239,68,68,0.10)',
    errorBorder: 'rgba(239,68,68,0.45)',
    successLight: 'rgba(34,197,94,0.10)',
    successBorder: 'rgba(34,197,94,0.40)',
    successText: '#86EFAC',
    warningLight: 'rgba(245,158,11,0.10)',
    warningBorder: 'rgba(245,158,11,0.40)',
    warningText: '#FCD34D',
    infoLight: 'rgba(96,165,250,0.10)',
    infoBorder: 'rgba(96,165,250,0.40)',
    infoText: '#93C5FD',
    actionHover: 'rgba(232, 232, 234, 0.06)',
    overlayShadow: '0 16px 40px rgba(0,0,0,0.6)',
  },
} as const;

export function getShellMuiTheme(mode: ThemeMode): Theme {
  const p = palettes[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: p.accent,
        light: p.accentHover,
        dark: p.accentPressed,
        contrastText: p.accentOn,
      },
      secondary: { main: p.textSecondary, contrastText: p.bgSurface },
      background: { default: p.bgCanvas, paper: p.bgSurface },
      text: {
        primary: p.textPrimary,
        secondary: p.textSecondary,
        disabled: p.textTertiary,
      },
      divider: p.borderSubtle,
      error: { main: p.error, light: p.errorLight, dark: p.error },
      warning: { main: p.warningText, light: p.warningLight },
      info: { main: p.infoText, light: p.infoLight },
      success: { main: p.successText, light: p.successLight },
      action: {
        hover: p.actionHover,
        selected: mode === 'dark' ? 'rgba(52, 211, 153, 0.12)' : 'rgba(16, 185, 129, 0.08)',
      },
    },
    typography: {
      fontFamily:
        "'Inter', 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      h1: { fontSize: 22, fontWeight: 600, lineHeight: 1.25, color: p.textPrimary },
      h2: { fontSize: 18, fontWeight: 600, lineHeight: 1.35, color: p.textPrimary },
      h3: { fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: p.textPrimary },
      h4: { fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: p.textPrimary },
      h5: { fontSize: 14, fontWeight: 600, lineHeight: 1.45, color: p.textPrimary },
      h6: { fontSize: 13, fontWeight: 600, lineHeight: 1.5, color: p.textPrimary },
      body1: { fontSize: 14, lineHeight: 1.55, fontWeight: 400, color: p.textPrimary },
      body2: { fontSize: 13, lineHeight: 1.5, fontWeight: 400, color: p.textSecondary },
      button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
      caption: { fontSize: 12, color: p.textTertiary },
    },
    shape: { borderRadius: 6 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '6px 12px',
            fontSize: 13,
            fontWeight: 500,
            minHeight: 32,
            boxShadow: 'none',
            transition: 'background 120ms ease-out, border-color 120ms ease-out',
            '&:hover': { boxShadow: 'none', transform: 'none' },
          },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          outlined: {
            borderColor: p.border,
            color: p.textPrimary,
            '&:hover': { borderColor: p.borderStrong, backgroundColor: p.bgHover },
          },
          text: {
            color: p.textSecondary,
            '&:hover': { backgroundColor: p.bgHover, color: p.textPrimary },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { borderRadius: 8, backgroundImage: 'none', boxShadow: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 8, boxShadow: 'none', border: `1px solid ${p.borderSubtle}` },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              backgroundColor: p.bgSurface,
              fontSize: 13,
              '& fieldset': { borderColor: p.border },
              '&:hover fieldset': { borderColor: p.borderStrong },
              '&.Mui-focused fieldset': { borderColor: p.accent, borderWidth: 1 },
              '&.Mui-error fieldset': { borderColor: p.error },
            },
            '& .MuiInputLabel-root': {
              fontSize: 13,
              color: p.textSecondary,
              '&.Mui-focused': { color: p.accent },
              '&.Mui-error': { color: p.error },
            },
            '& .MuiFormHelperText-root': { fontSize: 11, marginLeft: 2 },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { input: { padding: '8px 12px' } },
      },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiInputBase: { styleOverrides: { input: { fontSize: 13 } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, height: 22, fontSize: 11, fontWeight: 500, boxShadow: 'none' },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 6, fontSize: 13, alignItems: 'center', border: '1px solid' },
          standardError: {
            backgroundColor: p.errorLight,
            borderColor: p.errorBorder,
            color: p.error,
          },
          standardSuccess: {
            backgroundColor: p.successLight,
            borderColor: p.successBorder,
            color: p.successText,
          },
          standardWarning: {
            backgroundColor: p.warningLight,
            borderColor: p.warningBorder,
            color: p.warningText,
          },
          standardInfo: {
            backgroundColor: p.infoLight,
            borderColor: p.infoBorder,
            color: p.infoText,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: { color: p.accent, height: 4 },
          thumb: { width: 14, height: 14 },
          rail: { backgroundColor: p.borderSubtle, opacity: 1 },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: p.borderSubtle } } },
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: p.accent } } },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            minHeight: 36,
            color: p.textSecondary,
            '&.Mui-selected': { color: p.textPrimary },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${p.borderSubtle}`,
            boxShadow: p.overlayShadow,
          },
          option: { fontSize: 13 },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { color: p.textTertiary, '&.Mui-checked': { color: p.accent } },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: { color: p.textTertiary, '&.Mui-checked': { color: p.accent } },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked': { color: p.accent } },
          track: { '.Mui-checked.Mui-checked + &': { backgroundColor: p.accent } },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${p.borderSubtle}`,
            boxShadow: p.overlayShadow,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? '#0F1014' : '#1F2024',
            color: mode === 'dark' ? '#E8E8EA' : '#E8E8EA',
            fontSize: 11,
            borderRadius: 4,
            padding: '4px 8px',
            border: mode === 'dark' ? `1px solid ${p.borderSubtle}` : 'none',
          },
          arrow: { color: mode === 'dark' ? '#0F1014' : '#1F2024' },
        },
      },
    },
  });
}

// Default for legacy imports — переходный экспорт light-темы.
const shellMuiTheme = getShellMuiTheme('light');
export default shellMuiTheme;
